<template>
  <div class="mic-setting-container">
    <p class="smaller accent">
      {{ t('available_mics') }}
    </p>
    <span v-if="microphoneStore.loading" class="loading-wrap">
      <LoadingIcon size="icon-medium" />
    </span>
    <template v-if="microphoneList.length > 0">
      <ButtonDeviceMicInput
        v-for="microphone in microphoneList"
        :key="`microphone_${microphone.id}`"
        :microphone="microphone"
        :using-tss="usingTss && microphone.id !== 'system-audio-microphone'"
        :is-device-loading="isDeviceLoading"
        @change-device="switchMic"
        @open-voice="openVoiceModal"
        @delete-voice="deleteVoice"
      />
      <div class="divider" />
      <div class="mic-setting-container">
        <p class="smaller accent">{{ t('adjust_mic_sensitivity') }} ({{ micSensitivity }})</p>
        <MicInputControl v-model:volume-ratio="micSensitivity" />

        <template v-if="isSystemAudioMicrophoneEnabled">
          <div class="divider" />
          <p class="smaller accent">
            {{ t('adjust_system_audio_mic_sensitivity') }} ({{ systemAudioMicrophoneStore.micSensitivity }})
          </p>
          <MicInputControl
            :volume-ratio="systemAudioMicrophoneStore.micSensitivity"
            @update:volume-ratio="updateSystemAudioMicGain"
          />
        </template>
      </div>
    </template>

    <p v-else class="smallest">
      {{ t('no_mic_or_error') }}
    </p>
  </div>

  <Teleport to="body">
    <AddVoiceModal
      v-if="showVoiceModal && usingTss"
      :device="registerDevice"
      :long-voice="isLongVoice"
      :is-using-select-language="hasOptionLanguage"
      @close="closeVoiceModal"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, Ref, ref, watch, WatchStopHandle } from 'vue';
import { useI18n } from 'vue-i18n';

import { isEmpty } from 'lodash';
import { storeToRefs } from 'pinia';

import { fetchRegisteredDeviceId } from '@/api/apiService';
import AddVoiceModal from '@/components/domains/admin/common/AddVoiceModal.vue';
import ButtonDeviceMicInput from '@/components/domains/chat/common/ButtonDeviceMicInput.vue';
import MicInputControl from '@/components/domains/chat/common/MicInputControl.vue';
import LoadingIcon from '@/components/icons/IconLoading.vue';
import { useNotification } from '@/composables/shared/useNotification';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { QRCodeType } from '@/stores/chat';
import { useMicrophoneStore } from '@/stores/microphone';
import { useSocketStore } from '@/stores/socket';
import { useSystemAudioMicrophoneStore } from '@/stores/systemAudioMicrophone';
import { useToastStore } from '@/stores/toast.js';
import { IMicrophone, IMicrophoneWithVoice, MicrophoneMode } from '@/types/components';
import { stringToBase64UrlSafe } from '@/utils/encryptor.js';
import {
  ij_hasShownVoiceModal,
  ij_setCurrentPopupMicInput,
  ij_isSystemAudioMicrophoneEnabled,
} from '@/utils/injectionKeys';

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    usingTss?: boolean;
    mode?: MicrophoneMode;
    isUsingSelectLanguage?: boolean;
  }>(),
  {
    isVisible: false,
    usingTss: false,
    mode: '',
    isUsingSelectLanguage: false,
  }
);

const { t } = useI18n();

const microphoneStore = useMicrophoneStore();
const audioStore = useAudioStore();
const callStore = useCallStore();
const socketStore = useSocketStore();
const toastStore = useToastStore();
const { alertDeleteVoice } = useNotification();

const systemAudioMicrophoneStore = useSystemAudioMicrophoneStore();

const { micSensitivity } = storeToRefs(microphoneStore);
const { qrCodeInfo } = storeToRefs(callStore);

const setCurrentPopupMicInput = inject(ij_setCurrentPopupMicInput) as (popupName?: string) => void;
const hasShownVoiceModal = inject<Ref<boolean>>(ij_hasShownVoiceModal, ref(false));
const isSystemAudioMicrophoneEnabled = inject<Ref<boolean>>(ij_isSystemAudioMicrophoneEnabled, ref(false));

/* readonly */
const AUTO_CLOSE_COUNT: Readonly<number> = 10;

/* ref */
const existingDeviceId = ref<string>(''); // 목소리 등록전 기기 ID
const registerDevice = ref<IMicrophoneWithVoice>({} as IMicrophoneWithVoice); // 목소리 등록 기기
const showVoiceModal = ref<boolean>(false); // 목소리 등록 모달
const isAutoMicSensitivity = ref<boolean>(false); // 마이크 자동 감도 조절 여부

const autoCloseSecondsLeft = ref<number>(AUTO_CLOSE_COUNT); // 모달자동닫기 카운트
const autoCloseInterval = ref(); // 모달자동닫기 인터벌

const isDeviceLoading = ref<boolean>(false); // 기기변경 로딩
const hasOptionLanguage = ref<boolean>(false); // 언어선택 노출 여부
let isMicOffForAddVoice = false; // 음성등록을 위해 임시로 마이크를 껐는지
let disableWatcherSelectedMic: WatchStopHandle; // 선택된 마이크 존재여부 watcher 중단 함수

const microphoneList = computed<IMicrophone[] | IMicrophoneWithVoice[]>(() => {
  return props.usingTss ? microphoneStore.microphonesWithVoice : microphoneStore.microphones;
});

const isLongVoice = computed<boolean>(() => {
  return ['CONSULTATION_CALL', 'GENERAL_CALL_PARTNER', 'CONFERENCE_CALL_PARTNER'].includes(qrCodeInfo.value.qrCodeType);
});

/**
 * @desc 마이크를 변경합니다.
 * @param mic - IMicrophone
 * */
const switchMic = async (mic: Partial<IMicrophoneWithVoice>) => {
  if (isDeviceLoading.value) {
    return;
  }
  try {
    isDeviceLoading.value = true;
    const { success, data } = await fetchRegisteredDeviceId(stringToBase64UrlSafe(mic.label));

    if (success) {
      // 마이크 ID 등록
      const newDeviceId = data.deviceId;
      callStore.updateDeviceId(newDeviceId);

      // 마이크 감도 조절
      microphoneStore.controlMicGain();

      // 마이크 변경
      await microphoneStore.switchMicrophone(mic.id);
    }

    closeModalAutomatically();
  } catch (e) {
    console.log(`[F] error ${e.message}`);
  } finally {
    isDeviceLoading.value = false;
  }
};

/**
 * @desc 목소리 등록 모달을 열 수 있습니다.
 * @param device - 현재 선택한 마이크 정보를 보내줍니다.
 * @param isUserOpened -
 * **/
const openVoiceModal = async (device: Partial<IMicrophoneWithVoice>, isUserOpened: boolean = true) => {
  hasOptionLanguage.value = !isUserOpened && !localStorage.getItem('languageSaved') && props.isUsingSelectLanguage;

  toastStore.useHintBox(false);
  existingDeviceId.value = microphoneStore.selectedMicrophone.id;
  Object.assign(registerDevice.value, { ...device });

  if (audioStore.isSoundOn) {
    audioStore.toggleSound();
  }

  if (microphoneStore.isMicOn) {
    isMicOffForAddVoice = true; // 음성등록을 위해 임시로 마이크를 껐는지
    socketStore.sendMessageAudioStreamEnd();
    await microphoneStore.clearMic();
  }

  showVoiceModal.value = true;
};

// 마이크 입출력 이벤트 추가 - AS 소켓메시지 보내기
const sendSocketMessage = (data) => {
  const audioBytes = new Float32Array(data);

  socketStore.sendMessageAudioStreamChunk(new Uint8Array(audioBytes.buffer), 'as');
};

/**
 * @desc 목소리 등록 모달을 닫을 수 있습니다.
 * **/
const closeVoiceModal = async (isSuccess: boolean) => {
  toastStore.useHintBox(true);
  showVoiceModal.value = false;
  await initMicSettings();

  if (isSuccess) {
    await microphoneStore.initMic(registerDevice.value.id);
    toastStore.info(`[${registerDevice.value.label}] ${t('change_mic_tss_on')}`, 'TSS');

    if (microphoneStore.selectedMicrophone.id === registerDevice.value.id) {
      socketStore.sendMessageChangeDevice(microphoneStore.selectedMicrophone.voiceId);
    }
  } else {
    await microphoneStore.initMic(existingDeviceId.value);
  }

  // 음성등록을 위해서 자동으로 마이크를 껐을 경우 마이크를 다시 켜기
  if (isMicOffForAddVoice) {
    socketStore.sendMessageAudioStreamStart();
    microphoneStore.initMicEvent(sendSocketMessage);
    await microphoneStore.startMic(microphoneStore.selectedMicrophone.id);
    isMicOffForAddVoice = false;
  }

  existingDeviceId.value = '';
  Object.assign(registerDevice.value, {});
};

/**
 * @desc 등록된 목소리를 삭제합니다.
 * @param deviceId - 현재 선택한 마이크의 ID
 * **/
const deleteVoice = (deviceId: number) => {
  alertDeleteVoice(deviceId, async () => {
    if (microphoneStore.selectedMicrophone.voiceId === deviceId) {
      socketStore.sendMessageChangeDevice('0');
    }
    await microphoneStore.requestMicWithVoice();
  });
};

/**
 * @desc 10초간 행동이 없을 경우, 현재 모달을 닫습니다.
 * **/
const closeModalAutomatically = () => {
  autoCloseSecondsLeft.value = AUTO_CLOSE_COUNT;
  if (autoCloseInterval.value) {
    clearInterval(autoCloseInterval.value);
  }

  autoCloseInterval.value = setInterval(() => {
    autoCloseSecondsLeft.value -= 1;
    if (autoCloseSecondsLeft.value === 0) {
      setCurrentPopupMicInput();
      clearInterval(autoCloseInterval.value);
    }
  }, 1000);
};

const sendMessageSwitchDevice = async () => {
  await microphoneStore.requestMicWithVoice();
  socketStore.sendMessageChangeDevice(microphoneStore.selectedMicrophone.voiceId || '0');
};

const initMicSettings = async () => {
  await microphoneStore.initMicrophoneStore({
    mode: props.mode,
    isUsingMicWithVoice: props.usingTss,
    isUsingSpecificGain: true,
    isUsingDeviceAutoUpdate: true,
    isUsingHintBox: true,
    isUsingToast: true,
  });
  microphoneStore.initMicChangeEvent(sendMessageSwitchDevice);
  socketStore.sendMessageChangeDevice(microphoneStore.selectedMicrophone.voiceId || '0');
};

// 마이크 접근권한 획득 시
const handleGainMicAccess = async () => {
  const isModeConsultation = QRCodeType.isConsultationType(qrCodeInfo.value.qrCodeType); // 전화모드인지
  const isMicSelected = !isEmpty(microphoneStore.selectedMicrophone); // 선택된 마이크가 있는지
  const hasVoiceId = microphoneStore.microphonesWithVoice.some(({ voiceId }) => !!voiceId); // 등록된 음성이 있는지
  const hasVoiceRegister = qrCodeInfo.value.options?.displayVoiceRegister; // 현재 모드에 음성등록 기능이 있는지

  if (
    !isModeConsultation &&
    isMicSelected &&
    !hasVoiceId &&
    hasVoiceRegister &&
    microphoneStore.permissionGranted &&
    !hasShownVoiceModal.value
  ) {
    await openVoiceModal(microphoneStore.selectedMicrophone, false);
    hasShownVoiceModal.value = true;
  }

  disableWatcherSelectedMic?.();
};

const updateSystemAudioMicGain = (sensitivity: number) => {
  systemAudioMicrophoneStore.controlMicGain(sensitivity);
};

onMounted(async () => {
  closeModalAutomatically();
  await initMicSettings();
  await handleGainMicAccess();
});

onUnmounted(async () => {
  if (autoCloseInterval.value) {
    clearInterval(autoCloseInterval.value);
  }
  await microphoneStore.initMicrophoneStore();
});

watch(
  () => isDeviceLoading.value,
  (newValue) => {
    if (!newValue) {
      closeModalAutomatically();
    }
  }
);

// 게인 조절
watch(
  () => micSensitivity.value,
  () => {
    microphoneStore.controlMicGain();
    closeModalAutomatically();
  }
);

// 소켓 상태에 따른 마이크 컨트롤
watch(
  () => socketStore.socketState,
  async (state) => {
    switch (state) {
      case 'OPEN':
        await initMicSettings();
        break;
      case 'ERROR_NO_HEADER':
      case 'CLOSED':
      case 'RETRY_FAILED':
        await microphoneStore.initMicrophoneStore();
        break;
      default:
        break;
    }
  }
);

watch(
  () => props.isVisible,
  async (val) => {
    if (val) {
      microphoneStore.initMicChangeEvent(sendMessageSwitchDevice);
    }
    if (val && isEmpty(microphoneStore.selectedMicrophone)) {
      const { id } = microphoneStore.microphones.find((mic) => mic.default) || {};
      await microphoneStore.initMic(id);
    }
  }
);
</script>

<style scoped lang="scss">
.mic-setting-container {
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.title {
  margin-bottom: 0.25rem;
}

.loading-wrap {
  display: flex;
  justify-content: center;
  /* 가운데 정렬 */
  width: 100%;
  align-items: center;
  gap: 0.5rem;
  z-index: 0;
  overflow: visible;
}

.divider {
  margin-top: 0.5rem;
}
</style>
